import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useLocation } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import LookupPicker from '../additional_payments/lookup_picker';
import { useHeadingRefocus } from '../shared/use_heading_refocus';
import { toggle_sort } from '../shared/use_sortable';
import { LIMITS } from '../additional_payments/params';
import { fetch_additional_teachers } from '../api';
import { month_label } from '../format';
import { fetch_audit_csv, fetch_audit_facets, fetch_audit_list } from './api';
import {
  append_page,
  audit_error_view,
  audit_facets_view,
  audit_page_view,
  teacher_name_from_rows
} from './adapters';
import {
  audit_csv_fallback_name,
  audit_csv_params,
  audit_facets_params,
  audit_list_params,
  blank_audit_filters,
  has_audit_scope,
  parse_audit_deep_link,
  with_scope
} from './params';

/* audit-trail browser over teacher_payment_audit (/teacher-payments/audit, a hidden tab). */
const NO_FACETS = { entity_types: [], actions: [], actors: [] };

const initial_filters = (search, pay_month) => {
  const link = parse_audit_deep_link(search);
  if (has_audit_scope(link)) return { ...blank_audit_filters(), ...link };
  return blank_audit_filters(pay_month);
};

const ChangeList = ({ changes }) =>
  changes.length ? (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {changes.map((c, i) => (
        <li key={`${c.field}-${i}`}>
          <span className="tp-mono">{c.field}</span>: {c.old_text} → {c.new_text}
        </li>
      ))}
    </ul>
  ) : (
    <span className="tp-hint">No field changes recorded</span>
  );

const AuditTrailTab = ({ pay_month = '', months = [], on_error }) => {
  const location = useLocation();
  const [filters, setFilters] = useState(() => initial_filters(location.search, pay_month));
  const [facets, setFacets] = useState(NO_FACETS);
  const [facets_error, setFacetsError] = useState('');
  const [rows, setRows] = useState([]);
  const [next_before_id, setNextBeforeId] = useState(null);
  const [next_offset, setNextOffset] = useState(null);
  // null = newest first (keyset, before_id); {key, dir} = a column sort, paged by offset instead.
  const [sort, setSort] = useState(null);
  const [list_status, setListStatus] = useState('idle'); // idle | loading | ready | error
  const [list_error, setListError] = useState('');
  const [loading_more, setLoadingMore] = useState(false);
  const [more_error, setMoreError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [csv_error, setCsvError] = useState('');
  const [facets_rev, setFacetsRev] = useState(0);
  const [list_rev, setListRev] = useState(0);
  // Only the latest list/facets read may land; a slow earlier one must not overwrite it.
  const list_seq = useRef(0);
  const facets_seq = useRef(0);
  const heading_ref = useRef(null);
  const on_focus = useHeadingRefocus(heading_ref);

  // The shell's month arrives after its /months read; adopt it only while nothing is scoped yet.
  useEffect(() => {
    setFilters((f) => (has_audit_scope(f) ? f : blank_audit_filters(pay_month)));
  }, [pay_month]);

  // Returns the inline text, or '' once a permission failure was handed to the shell.
  const inline_error = useCallback(
    (e, fallback) => {
      const v = audit_error_view(e, fallback);
      if (v.kind === 'permission') {
        on_error(e, fallback);
        return '';
      }
      return v.text;
    },
    [on_error]
  );

  const scoped = has_audit_scope(filters);
  const { pay_month: scope_month, teacher_uuid: scope_teacher } = filters;

  // Facets depend on the scope only; entity/action/actor picks never re-read them.
  useEffect(() => {
    facets_seq.current += 1;
    const mine = facets_seq.current;
    setFacets(NO_FACETS);
    setFacetsError('');
    if (!scoped) return;
    fetch_audit_facets(audit_facets_params({ pay_month: scope_month, teacher_uuid: scope_teacher }))
      .then((data) => {
        if (mine === facets_seq.current) setFacets(audit_facets_view(data));
      })
      .catch((e) => {
        if (mine !== facets_seq.current) return;
        const text = inline_error(e, 'Could not load the filter values.');
        if (text) setFacetsError(text);
      });
  }, [scope_month, scope_teacher, scoped, facets_rev, inline_error]);

  useEffect(() => {
    list_seq.current += 1;
    const mine = list_seq.current;
    setRows([]);
    setNextBeforeId(null);
    setNextOffset(null);
    setListError('');
    setMoreError('');
    // An older page still in flight belongs to the old filters; its reply is dropped (list_seq).
    setLoadingMore(false);
    if (!scoped) {
      setListStatus('idle');
      return;
    }
    setListStatus('loading');
    fetch_audit_list(audit_list_params(filters, { sort }))
      .then((data) => {
        if (mine !== list_seq.current) return;
        const page = audit_page_view(data);
        setRows(page.rows);
        setNextBeforeId(page.next_before_id);
        setNextOffset(page.next_offset);
        setListStatus('ready');
      })
      .catch((e) => {
        if (mine !== list_seq.current) return;
        setListError(inline_error(e, 'Could not load the audit trail.'));
        setListStatus('error');
      });
  }, [filters, scoped, list_rev, inline_error, sort]);

  // Unsorted pages by before_id (keyset); a column sort pages by offset instead — never both.
  const load_more = () => {
    if (loading_more || (!sort && !next_before_id) || (sort && next_offset === null)) return;
    const mine = list_seq.current;
    setLoadingMore(true);
    setMoreError('');
    fetch_audit_list(audit_list_params(filters, { before_id: next_before_id, sort, offset: next_offset }))
      .then((data) => {
        if (mine !== list_seq.current) return;
        const page = audit_page_view(data);
        setRows((cur) => append_page(cur, page));
        setNextBeforeId(page.next_before_id);
        setNextOffset(page.next_offset);
        setLoadingMore(false);
      })
      .catch((e) => {
        if (mine !== list_seq.current) return;
        setMoreError(inline_error(e, 'Could not load older entries.'));
        setLoadingMore(false);
      });
  };

  const apply_sort = (key) => {
    setSort((s) => toggle_sort(s, key));
  };

  const download_csv = () => {
    if (downloading || !scoped) return;
    setDownloading(true);
    setCsvError('');
    fetch_audit_csv(audit_csv_params(filters))
      .then(({ csv, file_name }) => {
        if (!csv) {
          setCsvError('The CSV export came back empty. Try again.');
        } else {
          saveAs(
            new Blob([csv], { type: 'text/csv;charset=utf-8' }),
            file_name || audit_csv_fallback_name(filters)
          );
        }
        setDownloading(false);
      })
      .catch((e) => {
        setCsvError(inline_error(e, 'Could not download the audit trail.'));
        setDownloading(false);
      });
  };

  const set_filter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const search_teachers = useCallback(
    (q) => fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : [])),
    []
  );

  const month_options = months.map((m) => ({ value: m.pay_month, label: m.label }));
  if (filters.pay_month && !month_options.some((m) => m.value === filters.pay_month)) {
    month_options.unshift({ value: filters.pay_month, label: month_label(filters.pay_month) });
  }

  const teacher_text =
    filters.teacher_name ||
    teacher_name_from_rows(rows, filters.teacher_uuid) ||
    (filters.teacher_uuid ? `Teacher ${filters.teacher_uuid.slice(-8)}` : '');

  const columns = [
    { key: 'when', label: 'When (IST)', sort_key: 'time', render: (r) => r.when },
    {
      key: 'teacher',
      label: 'Teacher',
      sort_key: 'teacher_name',
      render: (r) => r.teacher_name || <span className="tp-hint">Not teacher-specific</span>
    },
    { key: 'month', label: 'Month', render: (r) => r.month },
    {
      key: 'entity',
      label: 'Record',
      sort_key: 'entity',
      render: (r) => (
        <span>
          {r.entity_label}
          {r.entity_id ? <span className="tp-hint tp-mono"> {r.entity_id}</span> : null}
        </span>
      )
    },
    { key: 'action', label: 'Action', sort_key: 'action', render: (r) => r.action },
    { key: 'actor', label: 'By', sort_key: 'actor', render: (r) => r.actor },
    { key: 'reason', label: 'Reason', render: (r) => r.reason },
    { key: 'changes', label: 'Changes', render: (r) => <ChangeList changes={r.changes} /> }
  ];

  const csv_off = downloading || !scoped;

  return (
    <div className="tp-au" onFocus={on_focus}>
      <div className="tp-card-hd">
        <span
          className="tp-card-hd-title"
          role="heading"
          aria-level={2}
          tabIndex={-1}
          ref={heading_ref}
        >
          Audit trail
        </span>
        <span className="tp-card-hd-sub">
          Every recorded change to teacher payments, newest first
        </span>
      </div>

      <div className="tp-frow">
        <select
          className="tp-select"
          aria-label="Filter by month"
          value={filters.pay_month}
          onChange={(e) => setFilters((f) => with_scope(f, { pay_month: e.target.value }))}
        >
          <option value="">Any month</option>
          {month_options.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <div style={{ width: '220px' }}>
          <LookupPicker
            id="tp-audit-teacher"
            name="teacher filter"
            label="Filter by teacher"
            label_hidden
            placeholder="Filter by name, slug or email"
            min_chars={LIMITS.TEACHER_QUERY_MIN_LEN}
            value_text={teacher_text}
            search={search_teachers}
            option_key={(t) => t.teacher_uuid}
            render_option={(t) => (
              <span>
                <b>{t.name}</b> · {t.email}
              </span>
            )}
            on_pick={(t) =>
              setFilters((f) =>
                with_scope(f, { teacher_uuid: t.teacher_uuid, teacher_name: t.name })
              )
            }
            on_clear={() =>
              setFilters((f) => with_scope(f, { teacher_uuid: '', teacher_name: '' }))
            }
          />
        </div>
        <select
          className="tp-select"
          aria-label="Filter by record type"
          value={filters.entity_type}
          onChange={(e) => set_filter('entity_type', e.target.value)}
        >
          <option value="">All record types</option>
          {facets.entity_types.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          aria-label="Filter by action"
          value={filters.action}
          onChange={(e) => set_filter('action', e.target.value)}
        >
          <option value="">All actions</option>
          {facets.actions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          aria-label="Filter by who made the change"
          value={filters.actor}
          onChange={(e) => set_filter('actor', e.target.value)}
        >
          <option value="">Anyone</option>
          {facets.actors.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="tp-frow-right">
          <button
            type="button"
            className="tp-btn-out"
            onClick={download_csv}
            aria-disabled={csv_off ? 'true' : undefined}
          >
            {downloading ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </div>

      {facets_error ? (
        <LoadFailed text={facets_error} on_retry={() => setFacetsRev((r) => r + 1)} />
      ) : null}
      {csv_error ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {csv_error}
        </div>
      ) : null}

      {!scoped ? (
        <div className="tp-empty">Choose a month or a teacher to see the audit trail.</div>
      ) : null}
      {list_status === 'loading' ? <CustomLoading /> : null}
      {list_status === 'error' && list_error ? (
        <LoadFailed text={list_error} on_retry={() => setListRev((r) => r + 1)} />
      ) : null}
      {list_status === 'ready' ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          cardTitle={(r) => (
            <span>
              <b>{r.entity_label}</b> · {r.action} · {r.when}
            </span>
          )}
          emptyText="No audit entries match these filters."
          sort={sort}
          on_sort={apply_sort}
        />
      ) : null}

      {list_status === 'ready' && more_error ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {more_error}
        </div>
      ) : null}
      {list_status === 'ready' && (next_before_id || next_offset !== null) ? (
        <div className="tp-frow" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="tp-btn-out"
            onClick={load_more}
            aria-disabled={loading_more ? 'true' : undefined}
          >
            {loading_more ? 'Loading…' : sort ? 'Load more' : 'Load older'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default AuditTrailTab;
